"use client";

import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import ChatPanel from "./ChatPanel";
import { useTwinChat } from "./useTwinChat";
import "./twin-chat.css";

const EASE_OUT = [0.16, 1, 0.3, 1];
const EASE_IN = [0.7, 0, 0.84, 0];

/**
 * 全站悬浮的 AI 分身入口：桌面端右下角卡片，移动端底部抽屉。
 * 对话状态和草稿放在这里，关闭面板不丢内容。
 */
export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const chat = useTwinChat();
  const launcherRef = useRef(null);
  const wasOpenRef = useRef(false);

  // 关闭后入口按钮重新挂载，此时再把焦点还给它
  useEffect(() => {
    if (!open && wasOpenRef.current) launcherRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div className="twin-surface">
      <AnimatePresence>
        {!open && (
          <motion.button
            ref={launcherRef}
            key="launcher"
            type="button"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.24, ease: EASE_OUT } }}
            exit={{ opacity: 0, y: 8, transition: { duration: 0.16, ease: EASE_IN } }}
            whileTap={{ scale: 0.97 }}
            className="twin-launcher-shadow fixed bottom-4 right-4 z-50 flex min-h-11 items-center gap-2 rounded-full border border-black/[0.06] bg-[#fffdfa]/95 pl-4 pr-5 text-sm font-semibold text-slate-900 backdrop-blur transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:border-white/10 dark:bg-[#12161f]/95 dark:text-white dark:hover:bg-[#171c27] sm:bottom-6 sm:right-6"
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <Sparkles size={18} strokeWidth={1.5} aria-hidden="true" />
            Ask Hailin
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              className="fixed inset-0 z-50 bg-black/30 sm:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.24, ease: EASE_OUT } }}
              exit={{ opacity: 0, transition: { duration: 0.18, ease: EASE_IN } }}
              onClick={close}
              aria-hidden="true"
            />
            <motion.div
              key="panel"
              role="dialog"
              aria-label="Chat with Hailin's AI twin"
              initial={{ opacity: 0, y: 16, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.28, ease: EASE_OUT } }}
              exit={{ opacity: 0, y: 16, scale: 0.98, transition: { duration: 0.2, ease: EASE_IN } }}
              style={{ transformOrigin: "bottom right" }}
              className="twin-panel-shadow fixed inset-x-0 bottom-0 z-50 h-[85svh] overflow-hidden rounded-t-3xl border border-black/[0.06] bg-[#fffdfa] dark:border-white/10 dark:bg-[#12161f] sm:inset-x-auto sm:bottom-6 sm:right-6 sm:h-[min(640px,calc(100svh-48px))] sm:w-[380px] sm:rounded-3xl"
            >
              <ChatPanel chat={chat} draft={draft} onDraftChange={setDraft} onClose={close} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
